import { useEffect } from 'react'
import { Routes, Route, useLocation } from 'react-router-dom'
import { useAppDispatch, useAppSelector } from '@/store/hooks'
import { store } from '@/store/hooks'
import { initializeAuth, logout } from '@/store/authSlice'
import { initializeApi } from '@/api'
import Layout from '@/components/layout/Layout'
import EnvRibbon from '@/components/layout/EnvRibbon'
import { getBreadcrumbs } from '@/utils/breadcrumbs'

// Auth Pages
import { LoginPage, RegisterPage, ResetPasswordPage, JoinPage, ImpersonatePage } from '@/pages/auth'

// Dashboard Pages
import { MyTaskDashboardPage, OverallDashboardPage, DetailDashboardPage } from '@/pages/dashboard'

// Project Pages
import {
  TaskManagementPage,
  PerformanceManagementPage,
  ReportManagementPage,
  MeetingManagementPage
} from '@/pages/project'

// Data Center Pages
import { StaffWorkloadPage, ChangeHistoryPage, AcademyInfoPage, DataExportPage } from '@/pages/data-center'

// Setting Pages
import { MemberManagementPage, WorkspaceManagementPage } from '@/pages/setting'

// Sample Page
import SamplePage from '@/pages/sample'
import PerformanceDashboardPage from './pages/dashboard/PerformanceDashboard'
import PeriodReportDashboardPage from '@/pages/dashboard/PeriodReportDashboard'
import StrategyDashboardPage from '@/pages/dashboard/StrategyDashboard'

function App() {
  const dispatch = useAppDispatch()
  const location = useLocation()
  const { isAuthenticated, isInitialized } = useAppSelector((state) => state.auth)

  useEffect(() => {
    initializeApi(store)
    dispatch(initializeAuth())
  }, [dispatch])

  // Clear localStorage when accessing /join or /reset-password page to ensure clean state
  useEffect(() => {
    if (location.pathname === '/join' || location.pathname === '/reset-password') {

      try {
        // Method 1: Complete localStorage clear
        localStorage.clear()

        // Method 2: Extra safety - remove specific DOT keys
        const knownKeys = [
          'dot_access_token',
          'dot_refresh_token',
          'dot_current_account',
          'dot_current_org_member',
          'dot_remember_email',
          'dot_selected_workspace',
          'dot_date_range',
          'dot_auth_remember_email',
          'dot_auto_login'
        ]

        knownKeys.forEach(key => {
          try {
            localStorage.removeItem(key)
          } catch (e) {
            console.error(`Failed to remove ${key}:`, e)
          }
        })

        // CRITICAL: Force logout to update Redux state immediately
        // This ensures isAuthenticated becomes false and auth routes are shown
        dispatch(logout())
      } catch (error) {
        console.error('App-level localStorage clear failed:', error)
      }
    }
  }, [location.pathname, dispatch])

  const handleMyActivity = () => {
    console.log('내활동 clicked')
  }

  const handleNotifications = () => {
    console.log('알림 clicked')
  }

  const handleRefresh = () => {
    window.location.reload()
  }

  const handleFullScreen = () => {
    const elem = document.documentElement
    
    if (!document.fullscreenElement) {
      // Enter fullscreen mode
      if (elem.requestFullscreen) {
        elem.requestFullscreen()
      } else if ((elem as any).webkitRequestFullscreen) {
        // Safari/Old Chrome
        (elem as any).webkitRequestFullscreen()
      } else if ((elem as any).mozRequestFullScreen) {
        // Firefox
        (elem as any).mozRequestFullScreen()
      } else if ((elem as any).msRequestFullscreen) {
        // IE/Edge
        (elem as any).msRequestFullscreen()
      }
    } else {
      // Exit fullscreen mode
      if (document.exitFullscreen) {
        document.exitFullscreen()
      } else if ((document as any).webkitExitFullscreen) {
        // Safari/Old Chrome
        (document as any).webkitExitFullscreen()
      } else if ((document as any).mozCancelFullScreen) {
        // Firefox
        (document as any).mozCancelFullScreen()
      } else if ((document as any).msExitFullscreen) {
        // IE/Edge
        (document as any).msExitFullscreen()
      }
    }
  }

  const handleAspectRatio = () => {
    console.log('화면비율 clicked')
  }

  const handleMoreOptions = () => {
    console.log('더보기 clicked')
  }

  if (!isInitialized) {
    return <div>Loading...</div>
  }

  return (
    <>
      <EnvRibbon />
      <Routes>
      {/* Impersonate - 인증 상태와 무관하게 접근 가능 */}
      <Route path="/impersonate" element={<ImpersonatePage />} />

      {isAuthenticated ? (
        <>
          {/* Application Routes - Layout 적용 */}
          <Route path="/*" element={
            <Layout
              headerProps={{
                breadcrumbs: getBreadcrumbs(location.pathname),
                onMyActivityClick: handleMyActivity,
                onNotificationClick: handleNotifications,
                onRefreshClick: handleRefresh
              }}
              sidebarProps={{
                onFullScreen: handleFullScreen,
                onAspectRatio: handleAspectRatio,
                onMoreOptions: handleMoreOptions
              }}
            >
              <Routes>
                <Route path="/" element={<MyTaskDashboardPage />} />

                {/* Dashboard Routes */}
                <Route path="/dashboard/mytask" element={<MyTaskDashboardPage />} />
                <Route path="/dashboard/overall" element={<OverallDashboardPage />} />
                <Route path="/dashboard/detail" element={<DetailDashboardPage />} />
                <Route path="/dashboard/performance" element={<PerformanceDashboardPage />} />
                <Route path="/dashboard/report/weekly" element={<PeriodReportDashboardPage period="weekly" />} />
                <Route path="/dashboard/report/monthly" element={<PeriodReportDashboardPage period="monthly" />} />
                <Route path="/dashboard/strategy" element={<StrategyDashboardPage />} />

                {/* Project Routes */}
                <Route path="/project/task" element={<TaskManagementPage />} />
                <Route path="/project/performance" element={<PerformanceManagementPage />} />
                <Route path="/project/report" element={<ReportManagementPage />} />
                <Route path="/project/meeting" element={<MeetingManagementPage />} />

                {/* Data Center Routes */}
                <Route path="/data/staff-workload" element={<StaffWorkloadPage />} />
                <Route path="/data/change-history" element={<ChangeHistoryPage />} />
                <Route path="/data/university" element={<AcademyInfoPage />} />
                <Route path="/data/export" element={<DataExportPage />} />

                {/* Setting Routes */}
                <Route path="/setting/member" element={<MemberManagementPage />} />
                <Route path="/setting/workspace" element={<WorkspaceManagementPage />} />
              </Routes>
            </Layout>
          } />
        </>
      ) : (
        <>
          {/* Auth Routes - Layout 없음 */}
          <Route path="/*" element={<LoginPage />} />
          <Route path="/register" element={<RegisterPage />} />
          <Route path="/reset-password" element={<ResetPasswordPage />} />
          <Route path="/join" element={<JoinPage />} />
        </>
      )}
      </Routes>
    </>
  )
}

export default App